import type { IconType } from 'react-icons';
import {
  BiBarChartAlt2,
  BiCog,
  BiFolderOpen,
  BiGroup,
  BiHomeAlt,
  BiListCheck,
  BiMoney,
  BiNews,
  BiSliderAlt,
  BiSolidGraduation,
  BiTrendingUp,
  BiWallet,
} from 'react-icons/bi';
import type { Role } from '@/types/shared';
import { FINANCE_NAV_ROLES } from '@/types/shared';
// Sidebar visibility must match the route-level RoleGuard exactly, or a role either sees a nav
// item that dead-ends in /403, or is denied a screen it should reach. Import the SAME read-role
// constants the router uses (`@/auth/roles`) rather than re-declaring the arrays here.
import {
  ACCOUNTS_READ_ROLES,
  EXAM_BOOKINGS_READ_ROLES,
  SALES_REPORT_ROLES,
  SALES_ROLES,
  SETTINGS_FINANCE_VIEW_ROLES,
  STUDENT_AFFAIRS_READ_ROLES,
} from '@/auth/roles';

export interface NavItem {
  key: string;
  /** i18n key under `nav.*` */
  labelKey: string;
  path: string;
  /** Roles that may at least *read* this module — 08-staff-ux.md §1 sidebar visibility table. */
  roles: Role[];
  /** Decorative-only icon (aria-hidden in Sidebar) — `react-icons/bi` (Bootstrap Icons), per
   * docs/design/12-reskin-spec.md §3 icon map — purely a visual scan aid alongside the
   * always-present text label. */
  icon: IconType;
}

const ALL_ROLES: Role[] = [
  'Admin',
  'Admissions Officer',
  'Student Affairs Officer',
  'Accounts Officer',
  'Sales Agent',
  'Sales Manager',
  'Exam Coordinator',
];

/**
 * Role-aware sidebar nav. A module a role cannot even read is omitted entirely (per the design
 * doc — never shown greyed-out). Every route besides `/dashboard` is currently a scaffold
 * placeholder (the real module UIs land in T30-T35); the important part here is that the *menu
 * itself* is already role-driven, ready to point at real screens later.
 */
export const NAV_ITEMS: NavItem[] = [
  {
    key: 'dashboard',
    labelKey: 'nav.dashboard',
    path: '/dashboard',
    roles: ALL_ROLES,
    icon: BiHomeAlt,
  },
  {
    key: 'students',
    labelKey: 'nav.students',
    path: '/students',
    roles: ALL_ROLES,
    icon: BiSolidGraduation,
  },
  {
    key: 'student-affairs',
    labelKey: 'nav.studentAffairs',
    path: '/student-affairs',
    roles: STUDENT_AFFAIRS_READ_ROLES,
    icon: BiFolderOpen,
  },
  {
    key: 'accounts',
    labelKey: 'nav.accounts',
    path: '/accounts',
    roles: ACCOUNTS_READ_ROLES,
    icon: BiMoney,
  },
  {
    key: 'sales',
    labelKey: 'nav.sales',
    path: '/leads',
    // Mirrors sales.controller.ts `STAFF_ROLES` (via the shared `SALES_ROLES` constant).
    roles: SALES_ROLES,
    icon: BiTrendingUp,
  },
  {
    key: 'sales-reports',
    labelKey: 'nav.salesReports',
    path: '/reports/sales',
    roles: SALES_REPORT_ROLES,
    icon: BiBarChartAlt2,
  },
  {
    key: 'exams',
    labelKey: 'nav.exams',
    path: '/exam-bookings',
    roles: EXAM_BOOKINGS_READ_ROLES,
    icon: BiListCheck,
  },
  {
    key: 'finance',
    labelKey: 'nav.finance',
    path: '/finance',
    // Admin + Accounts Officer (DEC-FIN1, 17-finance-ux.md §1.1) — a primary operational module,
    // not a Settings sub-screen (that's `settings-finance` below, config-only). Reuses the shared
    // RBAC constant rather than hardcoding the array a second time.
    roles: FINANCE_NAV_ROLES,
    // `bi-wallet2` -> `BiWallet` (verified export) — deliberately distinct from Accounts'
    // `BiMoney` so the two money-shaped nav items never look interchangeable at a glance.
    icon: BiWallet,
  },
  {
    // 2026-07: yearly installment-paid school/partner cost plans. Same finance-read tier as /finance.
    key: 'cost-plans',
    labelKey: 'nav.costPlans',
    path: '/finance/cost-plans',
    roles: FINANCE_NAV_ROLES,
    icon: BiMoney,
  },
  {
    // 2026-07: Admin-only P&L report (total income/costs/net + per-band breakdown).
    key: 'finance-report',
    labelKey: 'nav.financeReport',
    path: '/reports/finance',
    roles: ['Admin'],
    icon: BiBarChartAlt2,
  },
  {
    key: 'settings-users',
    labelKey: 'nav.settingsUsers',
    path: '/settings/users',
    roles: ['Admin'],
    icon: BiGroup,
  },
  {
    key: 'settings-lookups',
    labelKey: 'nav.settingsLookups',
    path: '/settings/lookups',
    roles: ['Admin'],
    icon: BiCog,
  },
  {
    key: 'settings-finance',
    labelKey: 'nav.settingsFinance',
    path: '/settings/finance',
    // View tier — Admin + Accounts Officer (2026-07 change). The accountant opens this screen
    // read-only; every edit control stays Admin-only inside the manager components. Mirrors the
    // route guard's `SETTINGS_FINANCE_VIEW_ROLES`.
    roles: SETTINGS_FINANCE_VIEW_ROLES,
    // `bi-sliders` has no `BiSliders` export in the installed react-icons/bi package (verified) —
    // `BiSliderAlt` is the closest actually-exported "sliders" glyph, still distinct from
    // `BiCog` (Lookups) and `BiGroup` (Users), per 12-reskin-spec.md §3's icon-verification rule.
    icon: BiSliderAlt,
  },
  {
    key: 'settings-content',
    labelKey: 'nav.settingsContent',
    path: '/settings/content',
    // Admin-only (T140/DEC-WC1/DEC-WC7) — mirrors the settings-users/settings-lookups/
    // settings-finance precedent; managing Parents' Opinions/FAQ has no purpose for non-Admin
    // roles, and every `/content/*` write is `@Roles('Admin')` server-side regardless.
    roles: ['Admin'],
    // `bi-news` -> `BiNews` (verified export) — distinct from every other Settings glyph
    // (`BiCog`/`BiGroup`/`BiSliderAlt`), reads as "editorial/public content" per
    // 12-reskin-spec.md §3's icon-verification rule.
    icon: BiNews,
  },
];

export function navItemsForRole(role: Role): NavItem[] {
  return NAV_ITEMS.filter((item) => item.roles.includes(role));
}
